import * as React from 'react';
import type { DocumentStyles } from '@cdf/api-zod';
import { Button } from '@/components/ui/button';
import {
  MAX_DOCUMENT_STYLES, MAX_WORD_STYLE_FILE_BYTES, mergeWordStyles, readWordStyles, WORD_STYLE_FILE_ACCEPT, wordStyleDestination,
  type ImportedWordStyle, type WordStyleRead
} from './word-style-import';

interface Props {
  styles: DocumentStyles;
  disabled?: boolean;
  /** Receives the unsaved draft with the chosen Word formatting applied. */
  onImported: (styles: DocumentStyles) => void;
}
interface FoundStyle { key: string; style: ImportedWordStyle }
interface FoundWordStyles { fileName: string; read: WordStyleRead; items: FoundStyle[] }

const list = (names: string[]) => names.length > 6 ? names.slice(0, 6).join(', ') + ' and ' + (names.length - 6) + ' more' : names.join(', ');
const describeFormatting = (style: ImportedWordStyle) => {
  const f = style.formatting;
  return [f.fontFamily + ' ' + f.fontSize + ' pt', f.bold && 'bold', f.italic && 'italic', f.underline && 'underlined', f.alignment !== 'left' && f.alignment]
    .filter(Boolean).join(', ');
};

/** Reads styles from a Word document or template in the browser, then lets the person choose which to apply to the draft. */
export function WordStyleImport({ styles, disabled = false, onImported }: Props) {
  const id = React.useId();
  const input = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [summary, setSummary] = React.useState<string[] | null>(null);
  const [found, setFound] = React.useState<FoundWordStyles | null>(null);
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(new Set());
  /** Anchor for Shift-click range selection. */
  const anchor = React.useRef<number | null>(null);

  async function readFile(file: File) {
    setError(null); setSummary(null); setFound(null);
    if (!/\.(docx|dotx)$/i.test(file.name)) { setError('Choose a Word document (.docx) or template (.dotx).'); return; }
    if (file.size > MAX_WORD_STYLE_FILE_BYTES) { setError('The Word file is larger than 25 MB. Choose a smaller file or template.'); return; }
    setBusy(true);
    try {
      const read = await readWordStyles(new Uint8Array(await file.arrayBuffer()));
      const used = new Map<string, number>();
      const items = read.styles.map(style => {
        const base = style.wordName.toLocaleLowerCase('en-US');
        const count = (used.get(base) ?? 0) + 1;
        used.set(base, count);
        return { key: count === 1 ? base : base + '#' + count, style };
      });
      setFound({ fileName: file.name, read, items });
      setSelected(new Set(items.map(item => item.key)));
      anchor.current = null;
    } catch (cause) {
      setError(cause instanceof Error ? 'The styles could not be read: ' + cause.message : 'The styles could not be read from this Word file.');
    } finally { setBusy(false); }
  }

  const rows = React.useMemo(() => found?.items.map(item => ({ ...item, destination: wordStyleDestination(styles, item.style) })) ?? [], [found, styles]);
  const chosen = rows.filter(row => selected.has(row.key));
  const additions = chosen.filter(row => row.destination.action === 'add').length;
  const overLimit = Math.max(0, styles.definitions.length + additions - MAX_DOCUMENT_STYLES);
  const allSelected = chosen.length > 0 && chosen.length === rows.length;

  const setKeys = (keys: string[], value: boolean) => setSelected(current => {
    const next = new Set(current);
    for (const key of keys) { if (value) next.add(key); else next.delete(key); }
    return next;
  });
  function toggleRow(index: number, range: boolean) {
    const key = rows[index]!.key, value = !selected.has(key);
    if (range && anchor.current !== null) {
      const from = Math.min(anchor.current, index), to = Math.max(anchor.current, index);
      setKeys(rows.slice(from, to + 1).map(row => row.key), value);
    } else setKeys([key], value);
    anchor.current = index;
  }
  function importSelected() {
    if (!found || chosen.length === 0) return;
    try {
      const merged = mergeWordStyles(styles, chosen.map(row => row.style));
      onImported(merged.styles);
      const lines = [chosen.length + ' of ' + rows.length + ' styles from "' + found.fileName + '" have been applied to this draft. Review them, preview, then save to keep them.'];
      if (merged.updated.length) lines.push('Updated: ' + list(merged.updated) + '.');
      if (merged.added.length) lines.push('Added: ' + list(merged.added) + '.');
      const fonts = new Set(chosen.map(row => row.style.formatting.fontFamily));
      const substitutions = found.read.fontSubstitutions.filter(item => fonts.has(item.used));
      if (substitutions.length) lines.push('Fonts not available here were replaced with the closest match: '
        + substitutions.map(item => item.word + ' → ' + item.used).join(', ') + '.');
      if (merged.skipped.length) lines.push('Not added because the 100-style limit was reached or the name is already used: ' + list(merged.skipped) + '.');
      setSummary(lines); setFound(null); setSelected(new Set());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The Word styles could not be applied. Your draft is unchanged.');
    }
  }

  return <section aria-label="Import styles from Word" className="space-y-2 rounded-md border p-3">
    <h3 className="text-sm font-semibold">Import from Word</h3>
    <p className="text-sm text-muted-foreground">Upload a Word document or template to see its paragraph styles, such as Normal, headings, Title, Quote and table text. Choose the styles you want: matching styles are updated and the others are added. The file is read on this device and is not uploaded or stored.</p>
    <input ref={input} id={id} type="file" accept={WORD_STYLE_FILE_ACCEPT} className="sr-only" tabIndex={-1} aria-hidden="true" data-testid="word-style-file"
      onChange={event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) void readFile(file);
      }} />
    {!found && <Button type="button" variant="outline" disabled={disabled || busy} onClick={() => input.current?.click()}>{busy ? 'Reading Word styles…' : 'Upload Word document or template'}</Button>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {summary && <div role="status" className="space-y-1 text-sm">{summary.map(line => <p key={line}>{line}</p>)}</div>}
    {found && <div className="space-y-3" role="group" aria-label={'Styles found in ' + found.fileName}>
      <p className="text-sm">{rows.length} styles found in "{found.fileName}". Tick the ones to import. Hold Shift and click to tick or untick a range.</p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b pb-2">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" className="size-4 accent-foreground" checked={allSelected} disabled={disabled} aria-label="Select all or none"
            ref={element => { if (element) element.indeterminate = chosen.length > 0 && !allSelected; }}
            onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map(row => row.key)))} />
          {allSelected ? 'Select none' : 'Select all'}
        </label>
        <Button type="button" variant="link" size="sm" className="h-auto p-0" disabled={disabled}
          onClick={() => setSelected(new Set(rows.filter(row => row.destination.action === 'update').map(row => row.key)))}>Only styles that update existing ones</Button>
        <Button type="button" variant="link" size="sm" className="h-auto p-0" disabled={disabled}
          onClick={() => setSelected(new Set(rows.filter(row => row.destination.action === 'add').map(row => row.key)))}>Only new styles</Button>
        <span className="ml-auto text-sm text-muted-foreground" aria-live="polite">{chosen.length} of {rows.length} selected</span>
      </div>
      <ul className="max-h-96 space-y-1 overflow-y-auto pr-1" aria-label="Styles in the Word file">
        {rows.map((row, index) => {
          const f = row.style.formatting, checkId = id + '-style-' + index;
          return <li key={row.key} className="flex items-start gap-3 rounded-md px-2 py-1.5 hover:bg-muted/50">
            <input id={checkId} type="checkbox" className="mt-1 size-4 accent-foreground" checked={selected.has(row.key)} disabled={disabled}
              onClick={event => { event.preventDefault(); toggleRow(index, event.shiftKey); }} onChange={() => undefined} />
            <label htmlFor={checkId} className="min-w-0 flex-1 cursor-pointer" onClick={event => { event.preventDefault(); if (!disabled) toggleRow(index, event.shiftKey); }}>
              <span className="block truncate" style={{ fontFamily: f.fontFamily, fontSize: Math.min(f.fontSize, 20) + 'pt', color: f.color, fontWeight: f.bold ? 700 : 400, fontStyle: f.italic ? 'italic' : 'normal', textDecoration: f.underline ? 'underline' : 'none' }}>{row.style.wordName}</span>
              <span className="block text-xs text-muted-foreground">
                {row.destination.action === 'update' ? 'Updates ' + row.destination.name : 'Adds new style ' + row.destination.name} · {describeFormatting(row.style)}
              </span>
            </label>
          </li>;
        })}
      </ul>
      {overLimit > 0 && <p role="alert" className="text-sm text-destructive">A document holds up to {MAX_DOCUMENT_STYLES} styles. {overLimit} of the new styles you selected will not be added; untick some or remove existing styles first.</p>}
      <div className="flex flex-wrap gap-2">
        <Button type="button" disabled={disabled || chosen.length === 0} onClick={importSelected}>Import selected ({chosen.length})</Button>
        <Button type="button" variant="outline" disabled={disabled} onClick={() => { setFound(null); setSelected(new Set()); }}>Cancel</Button>
        <Button type="button" variant="ghost" disabled={disabled || busy} onClick={() => input.current?.click()}>Choose a different file</Button>
      </div>
    </div>}
  </section>;
}
